'use client'

import {Suspense, useEffect, useState} from "react";
import {Card, CardContent} from "@/components/ui/card";
import DevPlaner from "@/assets/logo.png"
import Hero from "@/assets/login_hero.png"
import Image from "next/image";
import {Input} from "@/components/ui/input";
import {Checkbox} from "@/components/ui/checkbox";
import {Button} from "@/components/ui/button";
import {useRouter, useSearchParams} from "next/navigation";
import {useAuth} from "@/hooks/useAuth";
import {AuthApiError} from "@/lib/auth";
import {Toast} from "@/lib/toast";
import {AuthOverlayLoader} from "@/components/ui/AuthOverlayLoader";

function LoginPageContent() {
    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [remember, setRemember] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isBootstrapping, setIsBootstrapping] = useState<boolean>(true);

    const router = useRouter();
    const searchParams = useSearchParams();
    const forceLogin = searchParams.get("force") === "true";
    const {login: loginUser, loadMe} = useAuth();

    useEffect(() => {
        if (forceLogin) {
            setIsBootstrapping(false);
            return;
        }

        loadMe()
            .then((payload) => {
                if (!payload) {
                    return;
                }
                if (payload.currentWorkspace && !payload.workspaceSelectionRequired) {
                    router.replace("/dashboard");
                    return;
                }
                router.replace("/select-workspace?force=true");
            })
            .finally(() => {
                setIsBootstrapping(false);
            });
    }, [forceLogin, loadMe, router]);

    const onLogin = async () => {
        if (isLoading) {
            return;
        }

        setIsLoading(true);
        try {
            const result = await loginUser(email, password, remember);
            if (result.workspaceSelectionRequired) {
                router.push("/select-workspace?force=true");
                return;
            }
            router.push("/dashboard");
        } catch (e: unknown) {
            if (e instanceof AuthApiError) {
                Toast.error(e.payload?.message ?? "Anmeldung fehlgeschlagen");
            } else {
                Toast.error("Anmeldung fehlgeschlagen");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const onGoogleLogin = () => {
        window.location.href = `${process.env.NEXT_PUBLIC_API_URI}/auth/google`; // Passe den Pfad hier entsprechend an
    };

    if (isBootstrapping) {
        return <AuthOverlayLoader open={true} text={"Lade Benutzerdaten..."} />;
    }

    return (
        <Card className={"relative z-10 w-full max-w-5xl shadow-xl overflow-hidden"}>
            <CardContent className={"grid grid-cols-1 md:grid-cols-2 gap-0 p-0"}>
                <div className={"flex flex-col p-6 sm:p-10 lg:p-14 relative"}>
                    <div className={"flex flex-wrap justify-between items-center gap-3 font-bold"}>
                        <div className={"flex gap-3 items-center font-bold"}>
                            <Image src={DevPlaner} alt={"DevPlaner"} className={"w-6 h-6"} />
                            <span>
                                DevPlaner
                            </span>
                        </div>
                        <Button
                            variant={"outline"}
                            onClick={() => router.push("/")}
                            className={"text-sm"}
                        >
                            Zur Homepage
                        </Button>
                    </div>
                    <div className={"mt-8 sm:mt-10 text-2xl sm:text-3xl font-bold flex flex-col gap-1.5"}>
                        <h1>
                            Hallo,
                        </h1>
                        <h2>
                            Willkommen zurück!
                        </h2>
                    </div>
                    <p className={"mt-4 text-muted-foreground text-sm"}>
                        Melde dich hier mit deinen DevPlaner Daten an
                    </p>
                    <div className={"space-y-3 mt-8 sm:mt-10"}>
                        <div>
                            <label htmlFor={"email"} className={"sr-only"}>E-Mail Adresse</label>
                            <Input
                                type={"email"}
                                id={"email"}
                                placeholder={"E-Mail Adresse"}
                                value={email}
                                autoComplete={"email"}
                                onChange={(e) => setEmail(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") onLogin(); }}
                            />
                        </div>
                        <div>
                            <label htmlFor={"password"} className={"sr-only"}>Passwort</label>
                            <Input
                                type={"password"}
                                id={"password"}
                                placeholder={"Passwort"}
                                value={password}
                                autoComplete={"current-password"}
                                onChange={(e) => setPassword(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") onLogin(); }}
                            />
                        </div>
                    </div>
                    <div className={"flex flex-wrap items-center justify-between gap-2 mt-3"}>
                        <div className={"flex items-center gap-2 text-sm"}>
                            <Checkbox id={"remember"} onCheckedChange={(e) => setRemember(Boolean(e)) } value={String(remember)} />
                            <label htmlFor={"remember"} className={"cursor-pointer select-none"}>
                                Angemeldet bleiben
                            </label>
                        </div>
                        <Button
                            variant={"link"}
                            onClick={() => router.push("/reset-password")}
                            className={"p-0 h-auto text-sm"}
                        >
                            Passwort vergessen?
                        </Button>
                    </div>
                    <Button
                        variant={"default"}
                        onClick={onLogin}
                        disabled={isLoading}
                        className={"mt-6 w-full"}
                    >
                        {isLoading ? "Lädt..." : "Anmelden"}
                    </Button>

                    {/* Trennlinie
                    <div className="relative my-4">
                        <div className="absolute inset-0 flex items-center">
                            <span className="w-full border-t" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-background px-2 text-muted-foreground">Oder</span>
                        </div>
                    </div> */}

                        {/* Google Login Button
                        <Button
                            variant={"outline"}
                            onClick={onGoogleLogin}
                            disabled={isLoading}
                            className={"w-full flex items-center gap-2"}
                        >
                            <svg className="w-4 h-4" viewBox="0 0 24 24">
                                <path
                                    fill="currentColor"
                                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                />
                                <path
                                    fill="currentColor"
                                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                />
                                <path
                                    fill="currentColor"
                                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                                />
                                <path
                                    fill="currentColor"
                                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                                />
                            </svg>
                            Mit Google anmelden
                        </Button> */}

                    <div className={"mt-6 text-xs flex items-center justify-center gap-2 text-placeholder"}>
                        <span>
                            Noch kein Konto?
                        </span>
                        <Button
                            variant={"link"}
                            onClick={() => router.push("/sign-up")}
                            className={"p-0 text-xs h-auto"}
                        >
                            Hier registrieren
                        </Button>
                    </div>
                </div>
                <div className={"hidden md:block relative min-h-[520px]"}>
                    <Image src={Hero} alt={"Login Hero"} fill className={"object-cover"} priority={true} />
                </div>
            </CardContent>
        </Card>
    );
}

export default function LoginPage() {
    return (
        <div className={"relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-10"}>
            {/* feines Punkteraster als Textur */}
            <div
                className={"pointer-events-none absolute inset-0 text-foreground/5 [background-image:radial-gradient(currentColor_1px,transparent_1px)] [background-size:28px_28px]"}
            />
            {/* weiche Akzent-Glows in den Ecken */}
            <div className={"pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-primary/25 blur-[120px]"} />
            <div className={"pointer-events-none absolute -bottom-32 -right-24 h-96 w-96 rounded-full bg-primary/15 blur-[120px]"} />

            <Suspense fallback={<AuthOverlayLoader open={true} text={"Lade Login..."} />}>
                <LoginPageContent />
            </Suspense>
        </div>
    );
}
